import { useSession } from "@clerk/clerk-react";
import { Select, ModalBody, ModalHeader, ModalContent, Modal, SelectItem, Input, Textarea, ModalFooter, Spinner, Button, Tooltip } from "@heroui/react";
import { CheckCircle, Info, WarningCircle } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { Upload } from "components/upload/upload.component";
import { API_URL } from "constants/index";
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { BuildingDTO } from "shared/types/building";
import { CompanyContactPerson, ContactCompany } from "shared/types/contact";
import { DocumentType, ProjectPart, RenovationLabel, RenovationProject, DocumentApprovalStatus, DocumentSignStatus, MeasureType } from "shared/types/renovation";
import { UnitDTO } from "shared/types/unit";
import { format } from 'date-fns';
import DocumentLinesSection from "./components/documentLinesSection";
import { SupplierSearchSelect } from "./components/SupplierSearchSelect";
import { CompanyContactPicker } from "shared/components/contact";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    project?: RenovationProject;
    projectPart?: ProjectPart;
    building?: BuildingDTO;
    units?: UnitDTO[];
}

/**
 * Is dit bestandstype een bestelbon?
 *
 * Op het `financialDocumentType` van het bestandstype en niet op de naam: dat
 * veld is net bedoeld om te zeggen wát voor financieel document het is. De
 * terugval op de naam is er voor een bestandstype waar dat veld (nog) niet op
 * staat.
 */
const isPurchaseOrderLabel = (label: any): boolean =>
    label?.financialDocumentType === DocumentType.ORDER ||
    label?.label?.toLowerCase().trim() === 'bestelbon';

export interface DocumentFormData {
    supplier: ContactCompany | null;
    /**
     * Contactpersoon bij de leverancier. Bepaalt naar wie er over dit document
     * gecommuniceerd wordt; leeg betekent het algemene adres van de firma.
     */
    contactPerson: CompanyContactPerson | null;
    fileLabel: any | null;
    remarks: string;
    status: DocumentApprovalStatus;
    /**
     * Ondertekenstatus. Staat los van de goedkeuringsstatus: een bestelbon kan
     * goedgekeurd zijn en nog niet getekend, of net omgekeerd. Enkel zichtbaar
     * bij een financieel document — een werffoto teken je niet.
     */
    signedStatus: DocumentSignStatus;
    documentDate: string;
    label: RenovationLabel | null;
    building: BuildingDTO | null;
    units: UnitDTO[];
    projects: RenovationProject[];
    parts: ProjectPart[];
    partAmounts: Record<number, number>;
    unitAmounts: { [key: string]: number };
    documentNumber: string;
    vatAmount: number;
    totalExclVat: number;
    totalInclVat: number;
    validUntil?: Date;
    documentCategory?: string;
    lines: DocumentLine[];
    defaultVatPercentage: number;
}

interface AnalyticDistribution {
    unit: UnitDTO;
    percentage: number;
}

interface DocumentLine {
    description: string;
    analyticDistributions: AnalyticDistribution[];
    totalExclVat: number;
    vatAmount: number;
    vatPercentage: number;
    part: ProjectPart | null;
    totalInclVat: number;
    measure?: MeasureType;
    quantity?: number;
    pricePerMeasure?: number;
    remarks?: string;
    reference?: string;
    position: number;
}

export const DocumentUploadModal: React.FC<Props> = ({
    isOpen,
    onClose,
    onSuccess,
    project,
    projectPart,
    building,
    units
}) => {
    const { session } = useSession();

    // Add this line to fix "useEllaAnalysis not defined"
    const [useEllaAnalysis, setUseEllaAnalysis] = useState(false);
    
    // Add calculateTotals function back to fix the reference error
    const calculateTotals = useCallback((lines: any[]) => {
        return lines.reduce((acc, line) => ({
            totalExclVat: acc.totalExclVat + Number(line.totalExclVat || 0),
            vatAmount: acc.vatAmount + Number(line.vatAmount || 0),
            totalInclVat: acc.totalInclVat + Number(line.totalInclVat || 0)
        }), { totalExclVat: 0, vatAmount: 0, totalInclVat: 0 });
    }, []);

    // Add the missing state for formData here
    const [formData, setFormData] = useState<DocumentFormData>({
        supplier: null,
        contactPerson: null,
        remarks: '',
        status: DocumentApprovalStatus.RECEIVED,
        signedStatus: DocumentSignStatus.NOT_SIGNED,
        documentDate: format(new Date(), 'yyyy-MM-dd'),
        label: null,
        fileLabel: null,
        building: building || null,
        units: units || [],
        projects: project ? [project] : [],
        parts: projectPart ? [projectPart] : [],
        partAmounts: {},
        unitAmounts: {},
        documentNumber: '',
        vatAmount: 0,
        totalExclVat: 0,
        totalInclVat: 0,
        lines: [],
        defaultVatPercentage: 6
    });

    // Add state for selected units
    const [selectedUnits] = useState<any[]>(units || []);

    // Add state for file list
    const [fileList, setFileList] = useState<File[]>([]);

    // Add state for errors
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Auto-calculate amounts when total amount changes
    useEffect(() => {
        if (formData.totalInclVat > 0 && formData.parts.length > 0) {
            const amountPerPart = formData.totalInclVat / formData.parts.length;
            const newPartAmounts = formData.parts.reduce((acc, part) => ({
                ...acc,
                [part.id]: amountPerPart
            }), {});
            setFormData((prev: any) => ({ ...prev, partAmounts: newPartAmounts }));
        }
    }, [formData.totalInclVat, formData.parts]);

    // Auto-fill building/units when project changes
    useEffect(() => {
        if (project) {
            setFormData(prev => ({
                ...prev,
                building: project.building,
                units: project.units
            }));
        }
    }, [project]);

    // Auto-fill project info when part changes
    useEffect(() => {
        if (projectPart) {
            setFormData(prev => ({
                ...prev,
                project: projectPart.project,
                building: projectPart.project.building,
                units: projectPart.project.units
            }));
        }
    }, [projectPart]);

    // Add effect to handle unit amount distribution
    useEffect(() => {
        if (formData.totalInclVat > 0 && selectedUnits.length > 0) {
            const amountPerUnit = formData.totalInclVat / selectedUnits.length;
            const newUnitAmounts = selectedUnits.reduce((acc, unit) => ({
                ...acc,
                [unit.id]: amountPerUnit
            }), {});
            setFormData(prev => ({ ...prev, unitAmounts: newUnitAmounts }));
        }
    }, [formData.totalInclVat, selectedUnits]);

    // Add state for file labels
    const [fileLabels, setFileLabels] = useState<any[]>([]);
    const [filteredLabels, setFilteredLabels] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch file labels on mount
    useEffect(() => {
        const fetchFileLabels = async () => {
            try {
                const response = await fetchWithAuth(
                    `${API_URL}/file/labels/renovation`,
                    { method: "GET" },
                    session
                );
                const data = await response.json();
                // Map the data to include renovationFolders
                const labelsWithFolders = data.map((label: any) => ({
                    ...label,
                    folders: label.renovationFolders || [] // Ensure folders is always an array
                }));
                setFileLabels(labelsWithFolders);
                setFilteredLabels(labelsWithFolders);
            } catch (error) {
                toast.error("Failed to fetch file labels");
            }
        };
        fetchFileLabels();
    }, [session]);

    // Filter labels based on search term
    useEffect(() => {
        const filtered = fileLabels.filter(label =>
            label.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredLabels(filtered);
    }, [searchTerm, fileLabels]); // Only depend on search term and fileLabels

    // Add state for distribution errors
    const [distributionErrors, setDistributionErrors] = useState<{ [lineIndex: number]: string }>({});

    // Add a loading state for upload
    const [isUploading, setIsUploading] = useState(false);
    
    // State to hold available suppliers for the selected part
    const [availableSuppliers, setAvailableSuppliers] = useState<ContactCompany[]>([]);

    // Update the useEffect to set available suppliers when part changes
    useEffect(() => {
        if (formData.parts && formData.parts.length > 0) {
            const currentPart = formData.parts[0];
            if (currentPart.supplierAssignments && currentPart.supplierAssignments.length > 0) {
                const suppliers = currentPart.supplierAssignments.map(sa => sa.supplier);
                setAvailableSuppliers(suppliers);
                
                // If current supplier isn't in the list, reset it
                if (formData.supplier && !suppliers.some(s => s.id === formData.supplier?.id)) {
                    setFormData(prev => ({...prev, supplier: null}));
                }
            } else {
                setAvailableSuppliers([]);
            }
        }
    }, [formData.parts]);

    // Update validateForm to return the first critical error
    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.projects.length) newErrors.project = 'Selecteer een project';
        if (!formData.parts.length) newErrors.part = 'Selecteer een projectonderdeel';
        if (!formData.fileLabel) newErrors.fileLabel = 'Selecteer een bestandstype';
        if (!fileList.length) newErrors.file = 'Upload een bestand';
        if (!formData.documentDate) newErrors.documentDate = 'Vul een documentdatum in';
        if (!formData.lines?.length) newErrors.lines = 'Document moet minimaal één regel hebben';
        if (!formData.supplier) newErrors.supplier = 'Selecteer een leverancier';

        // Check if any line has distribution errors
        if (Object.keys(distributionErrors).length > 0) {
            newErrors.distributions = 'Corrigeer de kostenplaats verdelingen';
        }

        // Check if all lines have a part assigned
        const lineWithoutPart = formData.lines.find(line => !line.part);
        if (lineWithoutPart) {
            newErrors.lineParts = 'Elke regel moet aan een projectonderdeel gekoppeld zijn';
        }

        console.log('newErrors', newErrors);

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Update handleSubmit to show toast messages for validation failures
    const handleSubmit = async () => {
        console.log('handleSubmit triggered');
        
        // Explicitly run validation before proceeding
        const isValid = validateForm();
        console.log('Form validation result:', isValid, 'Errors:', errors);
        
        if (!isValid) {
            // Show toast messages for validation failures
            if (errors.project) {
                toast.error('Selecteer eerst een project');
                return;
            }
            
            if (errors.part) {
                toast.error('Selecteer eerst een projectonderdeel');
                return;
            }
            
            if (errors.supplier) {
                toast.error('Selecteer een leverancier voor dit document');
                return;
            }
            
            if (errors.fileLabel) {
                toast.error('Selecteer een bestandstype voor dit document');
                return;
            }
            
            if (errors.file) {
                toast.error('Upload eerst een bestand');
                return;
            }
            
            if (errors.documentDate) {
                toast.error('Vul de documentdatum in');
                return;
            }
            
            if (errors.lines) {
                toast.error('Document moet minimaal één regel hebben');
                return;
            }
            
            if (errors.lineParts) {
                toast.error('Elke regel moet aan een projectonderdeel gekoppeld zijn');
                return;
            }
            
            if (errors.distributions) {
                toast.error('De kostenplaats verdelingen moeten samen 100% zijn');
                return;
            }
            
            // Generic error if none of the above were triggered
            toast.error('Controleer het formulier op fouten');
            return;
        }
        
        // Set loading state
        console.log('Setting uploading state to true');
        setIsUploading(true);
        
        // Add a visible toast for upload start
        toast.info('Document wordt geüpload...');

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('file', fileList[0]);

            // Get supplier folder ID if available
            const currentPart = formData.parts[0];
            let supplierFolderId: string | undefined;

            if (currentPart && formData.supplier) {
                const supplierAssignment = currentPart.supplierAssignments?.find(
                    sa => sa.supplier.id === formData.supplier?.id
                );
                if (supplierAssignment?.supplierFolderId) {
                    supplierFolderId = supplierAssignment.supplierFolderId;
                }
            }
            
            // Prepare the data object
            const documentData = {
                type: formData.fileLabel.label || DocumentType.OTHER,
                status: formData.status,
                signedStatus: formData.signedStatus,
                documentNumber: formData.documentNumber,
                documentDate: formData.documentDate,
                remarks: formData.remarks,
                buildingId: formData.building?.id,
                fileLabel: formData.fileLabel,
                supplier: formData.supplier,
                contactPerson: formData.contactPerson,
                projects: formData.projects,
                parts: formData.parts,
                lines: formData.lines.map((line: DocumentLine) => ({
                    ...line,
                    partId: line.part?.id,
                    analyticDistributions: line.analyticDistributions.map(dist => ({
                        unit: { id: dist.unit.id },
                        percentage: dist.percentage
                    }))
                })),
                supplierFolderId,
                fileLabelFolders: formData.fileLabel?.folders || []
            };

            console.log('Sending document data:', documentData);

            formDataToSend.append('data', JSON.stringify(documentData));

            const response = await fetchWithAuth(
                `${API_URL}/renovation/document`,
                {
                    method: 'POST',
                    body: formDataToSend
                },
                session
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Upload failed');
            }

            toast.success('Document succesvol geüpload!');
            onSuccess();
            resetModal();
            onClose();
        } catch (error: any) {
            console.error('Error uploading document:', error);
            toast.error(`Fout bij uploaden document: ${error.message}`);
        } finally {
            console.log('Setting uploading state to false');
            setIsUploading(false);
        }
    };

    // Add state for available projects
    const [availableProjects, setAvailableProjects] = useState<RenovationProject[]>([]);

    // Fetch available projects on mount
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await fetchWithAuth(
                    `${API_URL}/renovation/project/building/${building?.id}`,
                    { method: "GET" },
                    session
                );
                const data = await response.json();
                setAvailableProjects(data);
            } catch (error) {
                toast.error("Failed to fetch projects");
            }
        };

        if (building?.id) {
            fetchProjects();
        }
    }, [building?.id]);

    // Add state for available units
    const [availableUnits, setAvailableUnits] = useState<UnitDTO[]>([]);

    // Update available units when building changes
    useEffect(() => {
        if (building?.id) {
            console.log(availableUnits);
            setAvailableUnits(building.units || []);
        }
    }, [building]);

    const createDefaultAnalyticDistributions = (units: any[]) => {
        const percentagePerUnit = units.length > 0 ? 1 / units.length : 0;
        return units.map(unit => ({
            unit,
            percentage: percentagePerUnit
        }));
    };

    // Add state for analysis loading
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Add these state variables at the top of the DocumentUploadModal component
    const [isFormValid, setIsFormValid] = useState(false);
    const [documentAnalyzed, setDocumentAnalyzed] = useState(false);
    const [showFileLabelDropdown, setShowFileLabelDropdown] = useState(false);

    // Add effect to check form validity
    useEffect(() => {
        console.log('isFormValid', isFormValid);
        console.log('documentAnalyzed', documentAnalyzed);
        setIsFormValid(Boolean(
            formData.projects.length > 0 && 
            formData.parts.length > 0 && 
            formData.fileLabel
        ));
    }, [formData.projects.length, formData.parts.length, formData.fileLabel]); // Only depend on specific values

    // Add this near the top of the component with other state declarations
    const [lastAnalysisResult, setLastAnalysisResult] = useState<any>(null);

    // Update handleFileUpload with more logging
    const handleFileUpload = useCallback(async (files: File[]) => {
        if (!files.length) return;

        setFileList(files);
        
        // Only analyze if Ella is enabled
        if (!useEllaAnalysis) {
            return; // Skip analysis if not using Ella
        }
        
        setIsAnalyzing(true);
        setDocumentAnalyzed(false);

        try {
            const uploadData = new FormData();
            uploadData.append('file', files[0]);
            
            // Log project part info
            console.log('Current project part:', formData.parts[0]);
            
            if (formData.parts[0]?.id) {
                uploadData.append('projectPartId', formData.parts[0].id.toString());
                console.log('Sending project part ID:', formData.parts[0].id);
                
                // Log available suppliers for this part
                console.log('Available suppliers for part:', 
                    formData.parts[0].supplierAssignments?.map(sa => ({
                        id: sa.supplier.id,
                        name: sa.supplier.name,
                        folderId: sa.supplierFolderId
                    }))
                );
            }

            const response = await fetchWithAuth(
                `${API_URL}/renovation/document/analyze`,
                {
                    method: 'POST',
                    body: uploadData
                },
                session
            );

            if (!response.ok) throw new Error('Analysis failed');

            const analysisResult = await response.json();
            console.log('Analysis result:', analysisResult);
            
            // Store the analysis result
            setLastAnalysisResult(analysisResult);

            // Get the units to use for distributions
            const unitsToUse = selectedUnits.length > 0 ? selectedUnits : (building?.units || []);
            console.log('Using units for distributions:', unitsToUse);
            
            const defaultDistributions = createDefaultAnalyticDistributions(unitsToUse);
            console.log('Created default distributions:', defaultDistributions);

            // Create the lines with proper calculations
            const lines = analysisResult.lines.map((line: any) => {
                const processedLine = {
                    description: line.description,
                    totalExclVat: Number(line.totalExclVat),
                    vatPercentage: Number(line.vatPercentage),
                    vatAmount: Number(line.vatAmount),
                    totalInclVat: Number(line.totalInclVat),
                    position: line.position,
                    part: formData.parts.length > 0 ? formData.parts[0] : null, // Associate with selected part
                    analyticDistributions: defaultDistributions
                };
                console.log('Processed line:', processedLine);
                return processedLine;
            });

            // Calculate totals using the memoized function
            const totals = calculateTotals(lines);
            console.log('Calculated totals:', totals);

            // Log supplier matching info if available
            if (analysisResult.supplierMatch) {
                console.log('Supplier match details:', {
                    matchedId: analysisResult.supplierMatch.matched_id,
                    confidence: analysisResult.supplierMatch.confidence,
                    detectedName: analysisResult.supplierMatch.detected_name,
                    matchedSupplier: analysisResult.supplier
                });
            }

            // Update form with analyzed data
            setFormData(prev => {
                const newFormData = {
                    ...prev,
                    documentDate: analysisResult.documentDate,
                    documentNumber: analysisResult.documentNumber,
                    supplier: analysisResult.supplier,
                    label: prev.parts[0]?.label || null,
                    lines,
                    totalExclVat: totals.totalExclVat,
                    vatAmount: totals.vatAmount,
                    totalInclVat: totals.totalInclVat
                };
                console.log('Updated form data:', newFormData);
                return newFormData;
            });

            setDocumentAnalyzed(true);
            console.log('Document analysis completed successfully');

        } catch (error) {
            console.error('Error analyzing document:', error);
            toast.error('Failed to analyze document');
        } finally {
            setIsAnalyzing(false);
        }
    }, [formData.parts, session, selectedUnits, calculateTotals, useEllaAnalysis]);

    // Add this cleanup function
    const resetModal = useCallback(() => {
        setFormData({
            supplier: null,
            contactPerson: null,
            remarks: '',
            status: DocumentApprovalStatus.RECEIVED,
            signedStatus: DocumentSignStatus.NOT_SIGNED,
            documentDate: format(new Date(), 'yyyy-MM-dd'),
            label: null,
            fileLabel: null,
            building: building || null,
            units: units || [],
            projects: project ? [project] : [],
            parts: projectPart ? [projectPart] : [],
            partAmounts: {},
            unitAmounts: {},
            documentNumber: '',
            vatAmount: 0,
            totalExclVat: 0,
            totalInclVat: 0,
            lines: [],
            defaultVatPercentage: 6
        });
        setFileList([]);
        setErrors({});
        setDistributionErrors({});
        setIsAnalyzing(false);
        setDocumentAnalyzed(false);
        setIsUploading(false); // Reset uploading state
        setSearchTerm('');
        setShowFileLabelDropdown(false);
    }, [building, units, project, projectPart]);

    // Update the useEffect to also run when isOpen changes
    useEffect(() => {
        if (isOpen && fileLabels.length > 0 && !formData.fileLabel) {
            const offerteLabel = fileLabels.find(label => 
                label.label.toLowerCase() === "offerte"
            );
            
            if (offerteLabel) {
                setFormData(prev => ({ ...prev, fileLabel: offerteLabel }));
                setSearchTerm("Offerte");
            }
        }
    }, [fileLabels, isOpen, formData.fileLabel]); // Add isOpen as a dependency

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                resetModal();
                onClose();
            }}
            size="5xl"
            scrollBehavior="inside"
            classNames={{
                base: "max-h-[85vh] min-h-[80vh]",
                body: "relative p-0", // Remove default padding for better control
                wrapper: "z-50"
            }}
        >
            <ModalContent>
                <ModalHeader className="flex flex-col gap-1 border-b border-neutral-primary/10 pb-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xl font-medium text-neutral-primary">Document uploaden</span>
                        {isAnalyzing && (
                            <div className="flex items-center gap-2 text-sm text-moss-primary bg-moss-primary/10 px-3 py-1 rounded-full">
                                <Spinner size="sm" color="success" />
                                Document wordt geanalyseerd...
                            </div>
                        )}
                    </div>
                </ModalHeader>
                <ModalBody>
                    <div className="p-6 space-y-6">
                        {/* Required Project Information Section */}
                        <div className="bg-F9F0EE/30 p-4 rounded-lg border border-neutral-primary/10 space-y-4">
                            <div className="text-base font-medium text-neutral-primary border-b pb-2 mb-2">
                                Project informatie
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                {/* Project Selection */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-neutral-primary">Project *</label>
                                    <Select
                                        items={availableProjects}
                                        selectedKeys={formData.projects.map(p => p.id.toString())}
                                        onChange={(e) => {
                                            const selectedProject = availableProjects.find(p => p.id.toString() === e.target.value);
                                            if (selectedProject) {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    projects: [selectedProject]
                                                }));
                                            }
                                        }}
                                        renderValue={(items) => {
                                            return items.map((item) => (
                                                <span key={item.key}>
                                                    RENO-{item.data?.id} - {item.data?.title}
                                                </span>
                                            ));
                                        }}
                                        classNames={{
                                            base: "w-full",
                                            trigger: "h-10 bg-white"
                                        }}
                                    >
                                        {(project) => (
                                            <SelectItem key={project.id}>
                                                RENO-{project.id} - {project.title}
                                            </SelectItem>
                                        )}
                                    </Select>
                                    {errors.project && (
                                        <p className="text-brick-primary text-sm mt-1">{errors.project}</p>
                                    )}
                                </div>

                                {/* Project Part Selection */}
                                {formData.projects.length > 0 && (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <label className="block text-sm font-medium text-neutral-primary">Standaard project onderdeel *</label>
                                            <Tooltip content="Dit is het standaard project onderdeel dat wordt ingevuld voor alle regels. Per regel kun je dit nog aanpassen indien nodig.">
                                                <Info className="h-5 w-5 text-neutral-secondary-mid" />
                                            </Tooltip>
                                        </div>
                                        <Select
                                            items={formData.projects[0].parts}
                                            selectedKeys={formData.parts.map(p => p.id.toString())}
                                            onChange={(e) => {
                                                const selectedPart = formData.projects[0].parts.find(
                                                    p => p.id.toString() === e.target.value
                                                );
                                                console.log('Selected part:', selectedPart); // Debug log
                                                if (selectedPart) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        parts: [selectedPart],
                                                        // Reset related fields when part changes
                                                        supplier: null,
                                                        lines: [],
                                                        partAmounts: {},
                                                        documentAnalyzed: false
                                                    }));
                                                    // Clear the part error when a part is selected
                                                    setErrors(prev => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors.part;
                                                        return newErrors;
                                                    });
                                                }
                                            }}
                                            renderValue={(items) => {
                                                return items.map((item) => (
                                                    <span key={item.key}>
                                                        RENO-{formData.projects[0].id}-O{item.data?.id} - {item.data?.title}
                                                    </span>
                                                ));
                                            }}
                                            classNames={{
                                                base: "w-full",
                                                trigger: "h-10 bg-white"
                                            }}
                                        >
                                            {(part) => (
                                                <SelectItem key={part.id}>
                                                    RENO-{formData.projects[0].id}-O{part.id} - {part.title}
                                                </SelectItem>
                                            )}
                                        </Select>
                                        {errors.part && (
                                            <p className="text-brick-primary text-sm mt-1">{errors.part}</p>
                                        )}
                                    </div>
                                )}

                                {/* Add Standard VAT Rate Selection */}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <label className="block text-sm font-medium text-neutral-primary">Standaard BTW tarief</label>
                                        <Tooltip content="Dit is het BTW-tarief dat standaard wordt ingevuld voor nieuwe regels. Per regel kun je dit nog aanpassen indien nodig.">
                                            <Info className="h-5 w-5 text-neutral-secondary-mid" />
                                        </Tooltip>
                                    </div>
                                    <Select
                                        selectedKeys={[`${formData.defaultVatPercentage}`]}
                                        onChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                defaultVatPercentage: Number(e.target.value)
                                            }));
                                        }}
                                        classNames={{
                                            base: "w-full",
                                            trigger: "h-10 bg-white"
                                        }}
                                    >
                                        <SelectItem key="21">21%</SelectItem>
                                        <SelectItem key="6">6%</SelectItem>
                                        <SelectItem key="0">0%</SelectItem>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        {/* Document Upload Section */}
                        <div className="bg-F9F0EE/30 p-4 rounded-lg border border-neutral-primary/10 space-y-4">
                            <div className="text-base font-medium text-neutral-primary border-b pb-2 mb-2">
                                Document uploaden
                            </div>
                            
                            {/* File Label Selection */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-neutral-primary">Bestandslabel *</label>
                                <div className="relative">
                                    <Input
                                        placeholder="Zoek bestandslabel..."
                                        value={formData.fileLabel ? formData.fileLabel.label : searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onFocus={() => setShowFileLabelDropdown(true)}
                                        className="w-full"
                                        classNames={{
                                            base: "w-full",
                                            inputWrapper: "bg-white h-10"
                                        }}
                                    />

                                    {showFileLabelDropdown && (
                                        <div
                                            className="absolute left-0 right-0 top-[calc(100%-1px)] z-1000 max-h-48 overflow-auto rounded-lg border border-neutral-primary/20 bg-white shadow-lg"
                                        >
                                            {filteredLabels
                                                .filter(label =>
                                                    label.label.toLowerCase().includes(searchTerm.toLowerCase())
                                                )
                                                .map((label) => (
                                                    <div
                                                        key={label.id}
                                                        onClick={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                fileLabel: label,
                                                                // Een bestelbon is per definitie goedgekeurd: je bestelt
                                                                // niet wat nog ter beoordeling ligt. Vandaar Goedgekeurd
                                                                // voorgevuld i.p.v. Ontvangen — nog steeds aanpasbaar.
                                                                status: isPurchaseOrderLabel(label)
                                                                    ? DocumentApprovalStatus.APPROVED
                                                                    : prev.status,
                                                            }));
                                                            setSearchTerm(label.label);
                                                            setShowFileLabelDropdown(false);
                                                        }}
                                                        className="flex items-center justify-between p-2 cursor-pointer hover:bg-neutral-100"
                                                    >
                                                        <span>{label.label}</span>
                                                        {label.renovationFolders?.length > 0 ? (
                                                            <CheckCircle className="h-5 w-5 text-moss-primary" />
                                                        ) : (
                                                            <WarningCircle className="h-5 w-5 text-brick-primary" />
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                                {errors.fileLabel && (
                                    <p className="text-brick-primary text-sm mt-1">{errors.fileLabel}</p>
                                )}
                            </div>

                            {/* Ella Analysis Toggle */}
                            <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-neutral-primary/10">
                                <input
                                    type="checkbox"
                                    checked={useEllaAnalysis}
                                    onChange={(e) => {
                                        setUseEllaAnalysis(e.target.checked);
                                        if (!e.target.checked) {
                                            // reset or remove analysis results if user toggles off
                                            setFileList([]);
                                            setDocumentAnalyzed(false);
                                        }
                                    }}
                                    className="h-4 w-4 text-moss-primary"
                                    id="ella-analysis-toggle"
                                />
                                <label htmlFor="ella-analysis-toggle" className="text-sm font-medium text-neutral-primary">
                                    Gebruik Ella om document automatisch te analyseren
                                </label>
                            </div>

                            {/* File Upload Component */}
                            <div className="mt-2">
                                <Upload
                                    label="Document bestand *"
                                    onChange={(files) => {
                                        setFileList(files);

                                        // If a file error was set previously, clear it now
                                        setErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.file; // remove "file" key from errors
                                            return newErrors;
                                        });

                                        // If user wants Ella analysis, call handleFileUpload
                                        if (useEllaAnalysis) {
                                            handleFileUpload(files);
                                        }
                                    }}
                                    maxCount={1}
                                    error={errors.file}
                                />
                            </div>
                        </div>

                        {/* Document Details Section */}
                        <div className="bg-F9F0EE/30 p-4 rounded-lg border border-neutral-primary/10 space-y-4">
                            <div className="text-base font-medium text-neutral-primary border-b pb-2 mb-2">
                                Document details
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {/* Company Selection - Search across all companies */}
                                <div>
                                    <label className="block text-sm font-medium text-neutral-primary mb-1">
                                        Leverancier *
                                    </label>
                                    <SupplierSearchSelect
                                        value={formData.supplier}
                                        onChange={(supplier) => setFormData(prev => ({
                                            ...prev,
                                            supplier,
                                            // Bij een andere leverancier hoort de vorige contactpersoon
                                            // niet meer: laten staan zou het document aan iemand van een
                                            // andere firma koppelen (en de backend weigert dat).
                                            contactPerson:
                                                prev.contactPerson?.companyId === supplier?.id
                                                    ? prev.contactPerson
                                                    : null,
                                        }))}
                                        linkedSuppliers={availableSuppliers}
                                        projectPartId={formData.parts[0]?.id}
                                        onSupplierLinked={(supplier) => {
                                            // Add the newly linked supplier to available suppliers
                                            setAvailableSuppliers(prev => [...prev, supplier]);
                                        }}
                                        placeholder="Zoek of maak leverancier..."
                                        error={errors.supplier}
                                    />
                                </div>

                                {/*
                                    Contactpersoon bij de gekozen leverancier: bepaalt naar wie er
                                    over dit document gemaild wordt. Enkel de personen van díe firma
                                    komen in de lijst, en er kan er ter plekke een toegevoegd worden.
                                */}
                                <div>
                                    <label className="block text-sm font-medium text-neutral-primary mb-1">
                                        Contactpersoon
                                    </label>
                                    {formData.supplier ? (
                                        <CompanyContactPicker
                                            companyId={formData.supplier.id}
                                            companyName={formData.supplier.name}
                                            companyEmail={formData.supplier.email}
                                            value={formData.contactPerson}
                                            onChange={(contact) => setFormData(prev => ({ ...prev, contactPerson: contact }))}
                                        />
                                    ) : (
                                        <p className="text-sm text-neutral-secondary-mid">
                                            Kies eerst een leverancier.
                                        </p>
                                    )}
                                </div>

                                {/* Document Date Field - Auto-filled from analysis */}
                                <div>
                                    <Input
                                        type="date"
                                        label="Document datum *"
                                        value={formData.documentDate}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            documentDate: e.target.value
                                        }))}
                                        startContent={formData.documentDate && <CheckCircle className="text-moss-primary" size={16} />}
                                        classNames={{
                                            base: "w-full",
                                            inputWrapper: "bg-white h-10"
                                        }}
                                    />
                                    {errors.documentDate && (
                                        <p className="text-brick-primary text-sm mt-1">{errors.documentDate}</p>
                                    )}
                                </div>

                                {/* Document Number Field - Auto-filled from analysis */}
                                <Input
                                    label="Document nummer"
                                    value={formData.documentNumber}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        documentNumber: e.target.value
                                    }))}
                                    startContent={formData.documentNumber && <CheckCircle className="text-moss-primary" size={16} />}
                                    classNames={{
                                        base: "w-full",
                                        inputWrapper: "bg-white h-10"
                                    }}
                                />

                                {/* Status Selection */}
                                <Select
                                    label="Status"
                                    selectedKeys={[formData.status]}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        status: e.target.value as DocumentApprovalStatus
                                    }))}
                                    classNames={{
                                        base: "w-full",
                                        trigger: "h-10 bg-white"
                                    }}
                                >
                                    {Object.values(DocumentApprovalStatus).map(status => (
                                        <SelectItem key={status}>
                                            {status}
                                        </SelectItem>
                                    ))}
                                </Select>

                                {/* Ondertekenstatus — de reden waarom een getekende
                                    bestelbon opgeladen wordt. Enkel bij een financieel
                                    document: een werffoto teken je niet. */}
                                {formData.fileLabel?.requiresFinancialDetails && (
                                    <Select
                                        label="Ondertekend"
                                        selectedKeys={[formData.signedStatus]}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            signedStatus: e.target.value as DocumentSignStatus
                                        }))}
                                        classNames={{
                                            base: "w-full",
                                            trigger: "h-10 bg-white"
                                        }}
                                    >
                                        {Object.values(DocumentSignStatus).map(signedStatus => (
                                            <SelectItem key={signedStatus}>
                                                {signedStatus}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                )}
                            </div>

                            {/* Remarks Field */}
                            <div className="mt-2">
                                <Textarea
                                    label="Opmerkingen"
                                    value={formData.remarks}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        remarks: e.target.value
                                    }))}
                                    minRows={3}
                                    classNames={{
                                        base: "w-full",
                                        inputWrapper: "bg-white"
                                    }}
                                />
                            </div>
                        </div>

                        {/* Document Lines Section - now a separate component */}
                        <DocumentLinesSection 
                            formData={formData} 
                            setFormData={setFormData} 
                            building={building as BuildingDTO} 
                        />

                        {/* Add this after the file upload component */}
                        {documentAnalyzed && (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <WarningCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-amber-800">
                                            KIJK DE DOCUMENT LIJNEN ZEER GOED NA OP LEVERANIER, DOCUMENTNUMMER, BEDRAG, BTW TARIEF en VOLLEDIGHEID. BEKIJK OOK OF HET TOTAAL OVEREENSTEMT MET DE OFFERTE.
                                        </p>
                                        <p className="text-sm text-amber-700 mt-1">
                                            De AI-analyse is een hulpmiddel, maar kan fouten bevatten. Controleer altijd zelf de gegevens.
                                        </p>
                                        <div className="mt-2">
                                            <Tooltip 
                                                content={
                                                    <div className="max-w-md p-2">
                                                        <p className="font-medium mb-1">Ella analyse resultaten:</p>
                                                        <pre className="text-xs bg-neutral-50 p-2 rounded-sm overflow-auto max-h-[200px]">
                                                            {JSON.stringify(lastAnalysisResult, null, 2)}
                                                        </pre>
                                                    </div>
                                                }
                                                placement="bottom"
                                            >
                                                <Button 
                                                    size="sm" 
                                                    variant="flat" 
                                                    className="bg-amber-100 text-amber-800"
                                                    startContent={<Info size={16} />}
                                                >
                                                    Bekijk analyse details
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter className="border-t border-neutral-primary/10 gap-2">
                    <ButtonNew
                        variant="secondaryBrick"
                        label="Annuleren"
                        onClick={() => {
                            resetModal();
                            onClose();
                        }}
                        isDisabled={isUploading}
                    />
                    <Button
                        className="bg-moss-primary text-white px-4 py-2 h-10 rounded-md font-medium hover:bg-moss-primary/90 transition-colors"
                        onClick={handleSubmit}
                        isDisabled={isUploading}
                        startContent={isUploading && (
                            <Spinner size="sm" color="white" />
                        )}
                    >
                        {isUploading ? "Document wordt opgeslagen..." : "Document opslaan"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}; 